/**
 * <collection-carousel-progress>
 *
 * Non-interactive scroll-progress indicator for the Collection Carousel redesign
 * (`sections/collection-list.liquid` with `layout_type: carousel`).
 *
 * Finds the sibling `<slideshow-slides>` scroll container in the same section and
 * reflects its horizontal scroll position onto a `--progress` custom property
 * (0–1). The visible fill widths itself from that value in CSS.
 *
 * Extends HTMLElement directly (not `@theme/component`) — it has no `ref=`
 * children and no declarative events, matching `ScrollHint` in `scrolling.js`.
 *
 * `Math.abs(scrollLeft)` normalises both RTL `scrollLeft` conventions (negative
 * in Chromium/Firefox, positive in Safari) to the same 0→1 range.
 */
class CollectionCarouselProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {number | null} */
  #rafId = null;

  #resizeObserver = new ResizeObserver(() => this.#update());

  connectedCallback() {
    this.#scroller =
      this.closest('.shopify-section')?.querySelector('slideshow-slides') ??
      this.closest('.section-resource-list')?.querySelector('slideshow-slides') ??
      null;

    if (!this.#scroller) {
      this.hidden = true;
      return;
    }

    this.#scroller.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.#resizeObserver.observe(this.#scroller);
    this.#update();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver.disconnect();

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #handleScroll = () => {
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const max = scroller.scrollWidth - scroller.clientWidth;

    // Nothing to scroll — no indicator.
    if (max <= 1) {
      this.hidden = true;
      return;
    }

    this.hidden = false;

    const position = Math.min(Math.abs(scroller.scrollLeft), max);
    const visibleFraction = Math.max(scroller.clientWidth / scroller.scrollWidth, 0.08);
    const progress = visibleFraction + (position / max) * (1 - visibleFraction);

    this.style.setProperty('--progress', progress.toFixed(4));
  };
}

if (!customElements.get('collection-carousel-progress')) {
  customElements.define('collection-carousel-progress', CollectionCarouselProgress);
}
